import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { renderPrice as currency } from 'core/utils';

// Import icons by individual modules for better code splitting
import CaretRight from 'core/icons/CaretRight';
import CheckMarkSVG from 'core/icons/CheckMarkSVG';
import Gift from 'core/icons/Gift';

import { Content } from 'core/styles';

import Spinner from 'core/components/Spinner';
import { Divider } from 'listing/layout';
import LastDayToOrder, { notInPast } from 'listing/components/LastDayToOrder/LastDayContainer';
import LastMinuteGift from 'listing/components/LastMinuteGift/LastMinuteGift';
import SellingFast from 'listing/components/SellingFast/SellingFast';
import OrderSoon from 'listing/components/OrderSoon/OrderSoon';
import ShippingDetails from 'listing/components/Shipping/ShippingDetails';
import CouponPicker from 'listing/components/CouponPicker/CouponPicker';
import { VariantsComponent } from 'listing/components/Variants';
import SoldOut from 'product/components/SoldOut';
import { TermsPicker } from 'product/components';
import { Surveys } from 'product/components';
import { getColor } from 'core/utils';

import {
	OtherDetails,
	Price,
	SubHeading,
	CurrencyCode,
	Interval,
	Button,
	ShippingScheduleWrapper,
	DiscountWrapper,
	Discount,
	DealWrapper,
	Deal,
	Subprice,
	ClaimWrapper,
	SpinWrapper,
	BorderBox,
	Title,
	PolicyBox,
	FlairTagBorderBox,
} from './pricingStyles';
import { ContactSeller } from 'core/components/ContactSeller';

const getShipsInDays = (days) => {
	if (days === null) return '';
	if (days === 1) return '24 hours';
	return `${days} days`;
};

const Pricing = ({
	lowestPrice,
	lowestVariantPrice,
	loadingPrice,
	retailValue,
	interval,
	otherDetails,
	shipping,
	buyBtnText,
	buyBtnProps,
	buyBtnLoading,
	gifting,
	giftBtnText,
	giftBtnProps,
	giftBtnLoading,
	promoCode,
	claimLink,
	productCurrentQuantity,
	badges,
	clean,
	variants,
	variantValues,
	onVariantChange,
	currencyCode,
	currencySymbol,
	shippingSchedule,
	variantCurrentQuantity,
	triggerLoginModal,
	isLoggedIn,
	email,
	hideCancellationPolicy,
	showTerms, // We can use that boolean for AB testing
	terms,
	surveys,
	flairTags,
	recentDeal,
	couponPicker,
	coupon,
	isProductPage,
	inCart,
	soldOut,
	soldOutProps, // sold out reminder notif button & modal
}) => {
	let isLowStock = flairTags?.filter(ft => ft.tag === 'low-stock').length > 0;
	let isOrderSoon = flairTags?.filter(ft => ft.tag === 'order-soon').length > 0;

	let orderByBadges = badges.filter(badge => badge.badge === 'order_by');
	// Same check we do to render < LastDayToOrder />
	const validOrderByBadges = orderByBadges.filter(({ data }) => {
		const { orderBy, arriveBy, arriveByName } = data;
		return (notInPast(orderBy) && (arriveBy || arriveByName));
	});
	// When this component is not used from ProductPageContainer, it will not have a lowest variant price.  Default to min_unit_term_price (lowestPrice)
	const price = typeof lowestVariantPrice !== 'undefined' ? lowestVariantPrice : lowestPrice;

	let isSoldOut = soldOut;
	const isProductSoldOutOverall = productCurrentQuantity !== null && productCurrentQuantity !== undefined && productCurrentQuantity <= 0;
	const isVariantSelectionSoldOut = variantCurrentQuantity!== null && variantCurrentQuantity!== undefined && variantCurrentQuantity <= 0;
	if (isProductSoldOutOverall || isVariantSelectionSoldOut) {
		isSoldOut = true;
	}

	// Legacy coupon banner, displays any coupon selected in listing editor on edition and unclaimed listing pages
	const showCouponBanner = !isProductPage;

	// Show coupon picker if there is a coupon - check IDs since these keys can contain empty values
	const showCouponPicker = coupon?.id || recentDeal?.coupon?.id;

	// Hide ships-by line in Shipping Policy if Christmas orderBy badge exists
	const hideShipsBy = orderByBadges?.filter(badge => badge.data.arriveByName === "Christmas").length > 0;

	return (
		<>
			<BorderBox id="buy-btn">
				{shippingSchedule && !orderByBadges.length && (
					<ShippingScheduleWrapper>
						Ships in <strong>{getShipsInDays(shippingSchedule)}</strong>
					</ShippingScheduleWrapper>
				)}

				{showCouponBanner && promoCode && (
					<DiscountWrapper>
						<Discount>{promoCode}</Discount>
					</DiscountWrapper>
				)}

				{showCouponBanner && recentDeal?.coupon?.description && (
					<DealWrapper>
						<Deal>{recentDeal?.coupon?.description}</Deal>
					</DealWrapper>
				)}

				{variants && (
					<Fragment>
						<VariantsComponent variants={variants} variantValues={variantValues} onVariantChange={onVariantChange}/>
						{variants.length > 0 && <Divider />}
					</Fragment>
				)}

				{!showTerms && (
					<>
						{!clean && !hideCancellationPolicy && <SubHeading>Plans as low as</SubHeading>}
						{loadingPrice ? (
							<SpinWrapper>
								<Spinner size="lg"/>
							</SpinWrapper>
						) : (
							<Price>
								{currency(price, currencySymbol)}
								{currencyCode !== 'USD' ? (<CurrencyCode> {currencyCode}</CurrencyCode>) : null}
								{interval && <Interval>{`/${interval}`}</Interval>}
							</Price>
						)}
					</>
				)}

				{showTerms && (
					<TermsPicker {...terms} currencyCode={currencyCode} currencySymbol={currencySymbol} isSoldOut={isSoldOut} />
				)}

				{retailValue &&
					<Subprice>{'('+ currency(retailValue) + '+ retail value)'}</Subprice>
				}

				{(!isSoldOut && surveys && surveys.showSurveys) && (
					<Surveys {...surveys} />
				)}

				{otherDetails && <OtherDetails>{otherDetails}</OtherDetails>}

				{showCouponPicker && !isSoldOut && (
					<CouponPicker {...couponPicker} />
				)}

				{!isSoldOut ? (
					<Button size="lg" fullWidth {...buyBtnProps} disabled={isSoldOut} className="listing-buy-btn-primary">
						{buyBtnText}
						{buyBtnLoading ? <Spinner center={false} color={getColor("black")} /> : (
							inCart ? <CheckMarkSVG className={"hasStroke"} stroke={"currentColor"} /> : <CaretRight stroke={"#fff"} fill={"currentColor"} />
						)}
					</Button>
				) : (
					<SoldOut {...soldOutProps} />
				)}

				{gifting && !isSoldOut && (
					<Button size="lg" themeType="stroke" fullWidth {...giftBtnProps} disabled={isSoldOut} className="listing-buy-btn-gift">
						{giftBtnText}
						{giftBtnLoading ? <Spinner center={false} color={"currentColor"} /> : (
							<Gift fill={'currentColor'} />
						)}
					</Button>
				)}

				{claimLink && (
					<ClaimWrapper>
						{claimLink}
					</ClaimWrapper>
				)}

				{/* Arrives after Christmas badge */}
				{/* set to false to hide at other times of the year */}
				{/* dont forget to also update it on listing/index if store level PDPs still exist */}
				{true && <LastMinuteGift />}

				{validOrderByBadges.length > 0 && <Divider />}
				{orderByBadges.length > 0 && orderByBadges.map(
					(badge, idx) => <LastDayToOrder key={`badge_${idx}`} {...badge.data} />
				)}
			</BorderBox>

			{(isLowStock || isOrderSoon) && (
				<FlairTagBorderBox>
					{isLowStock && (
						<SellingFast  />
					)}

					{(isOrderSoon && isLowStock) && (
						<br />
					)}

					{isOrderSoon && (
						<OrderSoon {...shipping} />
					)}
				</FlairTagBorderBox>
			)}


			{shipping && (
				<BorderBox>
					{shipping && (
						<PolicyBox>
							<Title>Shipping Policy</Title>
							<ShippingDetails {...shipping} hideShipsBy={hideShipsBy} />
						</PolicyBox>
					)}

					{!hideCancellationPolicy && (
						<PolicyBox>
							<Title>Cancellation &amp; Refund Policy</Title>
							<Content as="div">
								<ul>
									<li>
										<ContactSeller
											triggerLogin={triggerLoginModal}
											isLoggedIn={isLoggedIn}
											email={email}
											element="link"
											label={`Contact Seller for questions about your box`}
											notLoggedLabel={`Contact Seller for questions about your box`}
										/>
									</li>
									<li>Subscriptions automatically renew. You can cancel at any time.</li>
								</ul>
							</Content>
						</PolicyBox>
					)}
				</BorderBox>
			)}
		</>
	);
};

export const pricingProps = {
	claimLink: PropTypes.element,
	lowestPrice: PropTypes.number,
	lowestVariantPrice: PropTypes.number,
	loadingPrice: PropTypes.bool,
	retailValue: PropTypes.number,
	currencyCode: PropTypes.string,
	interval: PropTypes.string,
	otherDetails: PropTypes.string,
	shipping: PropTypes.object,
	shippingSchedule: PropTypes.number,
	promoCode: PropTypes.string,
	buyBtnLoading: PropTypes.bool,
	buyBtnText: PropTypes.string,
	buyBtnProps: PropTypes.object,
	gifting: PropTypes.bool,
	giftBtnLoading: PropTypes.bool,
	giftBtnText: PropTypes.string,
	giftBtnProps: PropTypes.object,
	badges: PropTypes.array,
	clean: PropTypes.bool,
	variants: PropTypes.array,
	variantValues: PropTypes.array,
	onVariantChange: PropTypes.func,
	soldOut: PropTypes.bool,
	dealDescription: PropTypes.string,
	currencySymbol: PropTypes.string,
	productCurrentQuantity: PropTypes.number,
	variantCurrentQuantity: PropTypes.number,
	triggerLoginModal: PropTypes.func,
	isLoggedIn: PropTypes.bool,
	email: PropTypes.string,
	hideCancellationPolicy: PropTypes.bool,
	showTerms: PropTypes.bool,
	terms: PropTypes.object,
	flairTags: PropTypes.array,
	recentDeal: PropTypes.object,
	couponPicker: PropTypes.object,
	coupon: PropTypes.object,
	isProductPage: PropTypes.bool,
	inCart: PropTypes.bool,
	surveys: PropTypes.object,
	soldOutProps: PropTypes.object,
};

Pricing.propTypes = pricingProps;

Pricing.defaultProps = {
	claimLink: null,
	buyBtnText: 'Buy Now',
	giftBtnText: 'Give as a Gift',
	buyBtnProps: {},
	giftBtnProps: {},
	badges: [],
	flairTags: [],
	currencyCode: "USD",
	currencySymbol: '$',
	isProductPage: true,
};

export default Pricing;
